import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Icons (mix of react-icons and local SVGs)
import {
    FiTrendingUp,
    FiArrowUpRight,
    FiStar, FiTarget, FiSearch,
    FiChevronRight,
    FiChevronLeft,
    FiCircle,
    FiDollarSign
} from 'react-icons/fi';
import { BsFire, BsCurrencyBitcoin, BsHexagon, BsDiamond, BsTriangle } from 'react-icons/bs';
import {
    SiTether,
    SiPolkadot,
    SiBinance,
    SiChainlink,
} from 'react-icons/si';

// Components
import Button from '../../../components/Button/Button';
import DynamicIconRenderer from '../../../components/DynamicIconRenderer/DynamicIconRenderer';

// Redux Actions
import { _getPublicStakingProducts, _getUserStakingPortfolio, _getStakingTags } from '../redux/actions';
import {
    setStakingPools,
    setStakingLoading,
    setPortfolioData,
    setPortfolioLoading,
    setStakingTags,
    setStakingTagsLoading,
    setStakingTagsError
} from '../redux/slices/stakingSlice';

// Helpers
import { formatAPR } from '../helpers/stakingHelpers';

// Styles
import Styles from './stakingMainPage.module.css';

const StakingMainPage = () => {
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedCategory, setSelectedCategory] = useState('all')
    const [currentPage, setCurrentPage] = useState(1)
    const coinsPerPage = 6

    // Navigation
    const navigate = useNavigate()

    // Redux
    const dispatch = useDispatch()
    const exchangeName = useSelector((state) => state.exchangeCustomization.value.name)
    const theme = useSelector((state) => state.theme.value)
    const stakingPools = useSelector((state) => state.staking.pools)
    const stakingLoading = useSelector((state) => state.staking.loading)
    const totalPools = useSelector((state) => state.staking.totalPools)
    const portfolioData = useSelector((state) => state.staking.portfolio.data)
    const portfolioLoading = useSelector((state) => state.staking.portfolio.loading)
    const stakingTags = useSelector((state) => state.staking.tags.data)
    const stakingTagsLoading = useSelector((state) => state.staking.tags.loading)
    const userData = useSelector((state) => state.userData.value)

    // Handle search input changes with debounce
    const [searchDebounce, setSearchDebounce] = useState(null)

    // Set page title
    document.title = `${exchangeName}: Staking`

    // Fetch staking tags from API
    const fetchStakingTags = () => {
        dispatch(setStakingTagsLoading(true))

        _getStakingTags(
            {}, // No filter for initial load - get all tags
            (response) => {
                console.log('Staking tags response:', response)

                // Transform API response to match expected format
                const transformedTags = [
                    // Always include "All Pools" as first option
                    { id: 'all', label: 'All Pools', tag: 'all', icon: 'FiTarget' },
                    // Add API tags - handle both possible response structures
                    ...response.map(apiTag => ({
                        id: apiTag.tag || apiTag.name || apiTag.id, // Use tag, name, or id as identifier
                        label: String(apiTag.tag || apiTag.name || 'Unknown'), // Ensure label is always a string
                        tag: apiTag.tag || apiTag.name || apiTag.id,
                        icon: apiTag.icon || 'FiTarget' // Use API icon or default
                    }))
                ]

                dispatch(setStakingTags(transformedTags))
            },
            (error) => {
                console.error('Error fetching staking tags:', error)
                dispatch(setStakingTagsError(error?.data?.message || 'Failed to fetch staking tags'))

                // No fallback data - just show "All Pools" if API fails
                const minimalTags = [{ id: 'all', label: 'All Pools', tag: 'all', icon: 'FiTarget' }]
                dispatch(setStakingTags(minimalTags))
            }
        )
    }

    // Fetch staking pools from API
    const fetchStakingPools = (page = 1, limit = 50, tagFilter = null, customSearchTerm = null) => {
        dispatch(setStakingLoading(true))

        // Use custom search term if provided, otherwise use current searchTerm
        // This allows us to pass empty string to clear search
        const currentSearchTerm = customSearchTerm !== null ? customSearchTerm : searchTerm
        const searchQuery = currentSearchTerm && currentSearchTerm.trim() ? currentSearchTerm.trim() : null

        // Prepare API params
        // searchQuery will be sent as search[currency_id] and search[currency_name] in the API action
        const params = {
            page,
            limit,
            searchQuery,
            tag: tagFilter // Add tag parameter for API filtering
        }

        // Add tag filter if API supports it and we have a selected tag other than 'all'
        const filterTag = tagFilter || selectedCategory
        console.log('API call with tag filter:', filterTag)

        _getPublicStakingProducts(
            params,
            (response) => {
                console.log('=== API RESPONSE DEBUG ===')
                console.log('Raw API response:', response)

                // Since the API might return an array directly, handle both cases
                const data = Array.isArray(response) ? response : response.data || []
                console.log('Extracted data array:', data)
                console.log('Data array length:', data.length)

                const transformedPools = transformPoolData(data)
                console.log('Transformed pools:', transformedPools)
                console.log('Transformed pools length:', transformedPools.length)

                dispatch(setStakingPools(transformedPools))
                dispatch(setStakingLoading(false))
                console.log('=== END API RESPONSE DEBUG ===')
            },
            (error) => {
                console.error('=== API ERROR ===')
                console.error('Error fetching staking pools:', error)
                console.error('Error details:', error?.response?.data)
                console.error('Error status:', error?.response?.status)
                console.error('==================')
                dispatch(setStakingLoading(false))

                // Temporary: Add some test data to see if the UI works
                console.log('Adding test data for debugging...')
                const testPools = [
                    {
                        id: 'test-btc',
                        coin: 'Bitcoin',
                        symbol: 'BTC',
                        color: '#f7931a',
                        iconUrl: null,
                        bestAPR: 5.5,
                        featured: false,
                        tags: [],
                        periods: ['Flexible', '30 Days', '60 Days'],
                        minAmount: 0.001,
                        maxAmount: 10,
                        rawData: { asset: 'BTC' }
                    }
                ]
                dispatch(setStakingPools(testPools))
            }
        )
    }

    // Fetch user staking portfolio
    const fetchUserPortfolio = () => {
        if (!userData.email) return // Only fetch if user is logged in

        dispatch(setPortfolioLoading(true))

        _getUserStakingPortfolio(
            (response) => {
                dispatch(setPortfolioData(response))
                dispatch(setPortfolioLoading(false))
            },
            (error) => {
                console.error('Error fetching user portfolio:', error)
                dispatch(setPortfolioLoading(false))
            }
        )
    }

    // Helper function to prepare tag filter for API
    const getTagFilter = (tag) => {
        // This will be used when the tag filtering API endpoint is available
        if (tag === 'all') return {}
        return { tag }
    }

    // Filter pools by tags on frontend
    const filterPoolsByTag = (pools, selectedTag) => {
        if (!pools || !Array.isArray(pools)) return []
        if (selectedTag === 'all') return pools

        return pools.filter(pool => {
            if (!pool) return false

            // Check if pool has tags array
            if (pool.tags && Array.isArray(pool.tags)) {
                return pool.tags.includes(selectedTag)
            }

            // Fallback logic until API provides proper tags
            const asset = pool.coin?.toUpperCase() || ''

            if (selectedTag === 'featured') {
                return pool.featured
            }
            if (selectedTag === 'high-yield') {
                return pool.bestAPR > 10 // Consider >10% as high yield
            }
            if (selectedTag === 'stable') {
                return ['USDT', 'USDC', 'DAI', 'BUSD'].includes(asset)
            }
            if (selectedTag === 'blue-chip') {
                return ['BTC', 'ETH'].includes(asset)
            }
            if (selectedTag === 'defi') {
                return !['BTC', 'ETH', 'USDT', 'USDC', 'DAI', 'BUSD'].includes(asset)
            }
            return false
        })
    }

    // Transform API data to match our component structure
    const transformPoolData = (pools) => {
        console.log('=== TRANSFORM POOL DATA DEBUG ===')
        console.log('transformPoolData called with:', pools)
        console.log('Is array?', Array.isArray(pools))
        console.log('Length:', pools?.length)

        if (!Array.isArray(pools)) {
            console.warn('Expected array of pools:', pools)
            return []
        }

        const transformedResults = pools.map((pool, index) => {
            console.log(`Processing pool ${index}:`, pool)

            if (!pool || !pool.period_summary_list) {
                console.warn(`Invalid pool data at index ${index}:`, pool)
                return null
            }

            // Get best APR from period_summary_list
            const bestAPR = pool.period_summary_list.reduce((max, period) => {
                const apr = parseFloat(period.apr || 0)
                return apr > max ? apr : max
            }, 0)

            // Get periods from period_summary_list and sort them properly
            const periods = pool.period_summary_list
                .map(period => ({
                    duration: period.duration,
                    type: period.type, // Use type from API
                    display: period.type === 'flexible' ? 'Flexible' : `${period.duration} Days`,
                    periodId: period.period_id,
                    apr: parseFloat(period.apr || 0),
                    minAmount: parseFloat(period.min_amount || 0),
                    maxAmount: parseFloat(period.max_amount || 0),
                    risk: period.risk,
                    reward: period.reward,
                    soldOut: period.sold_out,
                    features: period.features || []
                }))
                .sort((a, b) => {
                    // Flexible (type 'flexible') should come first
                    if (a.type === 'flexible') return -1;
                    if (b.type === 'flexible') return 1;
                    // Then sort by duration ascending
                    return a.duration - b.duration;
                });

            // Get min/max amounts across all periods
            const minAmount = Math.min(...pool.period_summary_list.map(p => parseFloat(p.min_amount || 0)))
            const maxAmount = Math.max(...pool.period_summary_list.map(p => parseFloat(p.max_amount || 0)))

            // Get available types
            const availableTypes = [...new Set(pool.period_summary_list.map(p => p.type))]

            // Determine if pool has both flexible and fixed options
            const hasFlexible = availableTypes.includes('flexible')
            const hasFixed = availableTypes.includes('fixed')

            // Process tags from API only - no mock data generation
            let tags = []

            // Use API-provided tags if available
            if (pool.tags && Array.isArray(pool.tags)) {
                tags = [...pool.tags]
            }
            // No fallback tag generation - only use what comes from API

            const transformedPool = {
                id: pool.asset,
                coin: getCoinName(pool.asset),
                symbol: pool.asset,
                color: getDefaultColor(pool.asset),
                iconUrl: pool.icon_url,
                bestAPR,
                featured: tags.includes('featured'),
                tags, // Store all tags for filtering
                periods, // Full period objects with all details
                periodsDisplay: periods.map(p => p.display), // Display strings for UI
                minAmount,
                maxAmount,
                availableTypes,
                hasFlexible,
                hasFixed,
                estAPR: parseFloat(pool.est_apr || 0), // Estimated APR from pool level
                rawData: pool
            }

            console.log(`Transformed pool ${index}:`, transformedPool)
            return transformedPool
        }).filter(Boolean); // Remove null entries

        console.log('Final transformed results:', transformedResults)
        console.log('=== END TRANSFORM POOL DATA DEBUG ===')
        return transformedResults
    }

    // Get full coin name from asset symbol
    const getCoinName = (asset) => {
        const coinNames = {
            'BTC': 'Bitcoin',
            'ETH': 'Ethereum',
            'SOL': 'Solana',
            'TRX': 'TRON',
            'FLR': 'Flare',
            'ADA': 'Cardano',
            'DOT': 'Polkadot',
            'USDT': 'Tether USD',
            'USDC': 'USD Coin',
            'BNB': 'Binance Coin',
            'MATIC': 'Polygon',
            'LINK': 'Chainlink',
            'AVAX': 'Avalanche'
        };
        return coinNames[asset] || asset;
    };

    // Default icon and color mapping for fallback
    const getDefaultIcon = (currencyId) => {
        console.log('getDefaultIcon called with:', currencyId)
        const Icon = (() => {
            switch (currencyId?.toLowerCase()) {
                case 'btc': return BsCurrencyBitcoin;
                case 'eth': return BsDiamond;
                case 'sol': return BsHexagon;
                case 'trx': return BsTriangle;
                case 'flr': return BsFire;
                case 'ada': return BsHexagon;
                case 'dot': return SiPolkadot;
                case 'usdt': return SiTether;
                case 'bnb': return SiBinance;
                case 'matic': return BsTriangle;
                case 'link': return SiChainlink;
                case 'avax': return BsTriangle;
                case 'atom': return FiCircle;
                case 'usdc': return FiDollarSign;
                case 'algo': return FiCircle;
                case 'xtz': return BsHexagon;
                case 'near': return FiCircle;
                default: return FiCircle;
            }
        })();
        return <Icon size={24} />;
    }

    const getDefaultColor = (currencyId) => {
        const colorMap = {
            'btc': '#f7931a',
            'eth': '#627eea',
            'sol': '#9945ff',
            'trx': '#ff0013',
            'flr': '#e84142',
            'ada': '#0033ad',
            'dot': '#e6007a',
            'usdt': '#26a17b',
            'bnb': '#f3ba2f',
            'matic': '#8247e5',
            'link': '#375bd2',
            'avax': '#e84142',
            'atom': '#2e3148',
            'usdc': '#2775ca',
            'algo': '#000000',
            'xtz': '#2c7df7',
            'near': '#00c08b'
        }
        return colorMap[currencyId?.toLowerCase()] || '#009999'
    }

    // This function has been replaced by direct tag handling in transformPoolData

    // Helper to get minimum amount from period list
    const getMinAmount = (periods) => {
        if (!periods || !Array.isArray(periods) || periods.length === 0) return 0

        const minAmounts = periods.map(period => parseFloat(period.min_amount || 0))
        return Math.min(...minAmounts)
    }

    // Helper to get maximum amount from period list
    const getMaxAmount = (periods) => {
        if (!periods || !Array.isArray(periods) || periods.length === 0) return null

        const maxAmounts = periods.map(period => parseFloat(period.max_amount || 0))
        return Math.max(...maxAmounts)
    }

    // Helper to get icon component dynamically
    const getIconComponent = (iconName, tagName) => {
        // If iconName is provided from API, use dynamic renderer
        if (iconName) {
            return (
                <DynamicIconRenderer
                    iconName={iconName}
                    size={16}
                    fallback={<FiCircle size={16} />}
                />
            );
        }

        // Fallback to tag-based mapping for backward compatibility (if no iconName from API)
        const tagLower = tagName?.toLowerCase() || '';
        let fallbackIconName = 'FiTarget'; // Default fallback

        if (tagLower.includes('featured')) fallbackIconName = 'FaCrown';
        else if (tagLower.includes('trending') || tagLower.includes('hot')) fallbackIconName = 'BsFire';
        else if (tagLower.includes('secure')) fallbackIconName = 'FaLock';
        else if (tagLower.includes('high yield')) fallbackIconName = 'FaChartLine';
        else if (tagLower.includes('blue chip')) fallbackIconName = 'FaGem';
        else if (tagLower.includes('new')) fallbackIconName = 'FiZap';
        else if (tagLower.includes('stable')) fallbackIconName = 'FiTarget';
        else if (tagLower.includes('reward')) fallbackIconName = 'FiAward';

        return (
            <DynamicIconRenderer
                iconName={fallbackIconName}
                size={16}
                fallback={<FiCircle size={16} />}
            />
        );
    }

    // Define available tags - use API data only, no fallback
    const availableTags = stakingTags && stakingTags.length > 0 ?
        stakingTags.map(tag => ({
            ...tag,
            icon: getIconComponent(tag.icon, tag.tag)
        })) :
        [
            // Only show "All Pools" if no API data
            { id: 'all', label: 'All Pools', icon: <DynamicIconRenderer iconName="FiTarget" size={16} fallback={<FiCircle size={16} />} /> }
        ]

    // Since we're now using API filtering, we don't need frontend filtering
    // Just use the pools directly from Redux
    const filteredPools = stakingPools || []

    // Calculate pagination based on filtered results
    const totalPages = Math.ceil(filteredPools.length / coinsPerPage)
    const startIndex = (currentPage - 1) * coinsPerPage
    const endIndex = Math.min(startIndex + coinsPerPage, filteredPools.length)
    const currentPools = filteredPools.slice(startIndex, endIndex)

    // Debug logging (after all variables are declared)
    console.log('=== STAKING MAIN PAGE DEBUG ===')
    console.log('stakingPools from Redux:', stakingPools)
    console.log('stakingLoading:', stakingLoading)
    console.log('selectedCategory:', selectedCategory)
    console.log('Filtered pools:', filteredPools)
    console.log('Current pools for display:', currentPools)
    console.log('================================')

    // Initial load
    useEffect(() => {
        fetchStakingTags()
        fetchStakingPools(1, 50)
        fetchUserPortfolio()
    }, [])

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (searchDebounce) {
                clearTimeout(searchDebounce)
            }
        }
    }, [searchDebounce])

    // Reset to page 1 when category changes (frontend filtering)
    useEffect(() => {
        setCurrentPage(1)
    }, [selectedCategory])

    const handleSearchChange = (value) => {
        setSearchTerm(value)

        // Clear existing timeout
        if (searchDebounce) {
            clearTimeout(searchDebounce)
        }

        // If search is cleared (empty), immediately fetch without search params
        if (!value || value.trim() === '') {
            setCurrentPage(1) // Reset to first page
            fetchStakingPools(1, 50, null, '') // Pass empty string to clear search
            return
        }

        // Set new timeout for 1 second
        const debounceTimer = setTimeout(() => {
            // Trigger API call after 1 second of no typing
            setCurrentPage(1) // Reset to first page
            fetchStakingPools(1, 50, null, value) // Pass the search value
        }, 1000) // 1 second delay

        setSearchDebounce(debounceTimer)
    }

    // Handle tag selection
    const handleCategoryChange = (tag) => {
        setSelectedCategory(tag)
        setCurrentPage(1)

        // Call API with tag filter
        console.log('Tag selected:', tag)
        if (tag === 'all') {
            // Fetch all pools without tag filter
            fetchStakingPools(1, 50, null)
        } else {
            // Fetch pools with specific tag filter
            fetchStakingPools(1, 50, tag)
        }
    }

    // Handle pagination
    const handlePageChange = (newPage) => {
        setCurrentPage(newPage)
    }

    // Handle staking pool selection
    const handleStartStaking = (pool) => {
        if (!pool.symbol) {
            console.error('Invalid pool data:', pool);
            return;
        }
        navigate(`/staking/${pool.symbol.toLowerCase()}`)
    }

    // Handle calculator navigation
    const handleCalculatorClick = () => {
        navigate('/staking/calculator')
    }

    // Handle portfolio navigation
    const handlePortfolioClick = () => {
        navigate('/account/staking/portfolio')
    }

    return (
        <div className={`pageContainer ${Styles.stakingContainer}`}>
            {/* Hero Section */}
            <div className={Styles.heroSection}>
                <div className={Styles.heroContent}>
                    <div className={Styles.heroHeader}>
                        <div className={Styles.heroIcon}>
                            <FiTrendingUp size={24} />
                        </div>
                        <h1 className={Styles.heroTitle}>
                            Crypto Staking
                        </h1>
                    </div>

                    <p className={Styles.heroDescription}>
                        Staking lets you earn rewards by holding certain cryptocurrencies. It works like earning interest on savings — your crypto helps secure the blockchain, and you get paid in return.
                    </p>

                    <div className={Styles.heroButtons}>
                        <Button
                            text={
                                <span className={Styles.buttonContent}>
                                    Start Staking
                                    <FiArrowUpRight size={16} />
                                </span>
                            }
                            variant="light"
                            className={Styles.primaryButton}
                            fitContent={true}
                            onClick={() => document.getElementById('staking-pools')?.scrollIntoView({ behavior: 'smooth' })}
                        />

                        {userData.email && (
                            <Button
                                text={
                                    <span className={Styles.buttonContent}>
                                        My Portfolio
                                        <FiArrowUpRight size={16} />
                                    </span>
                                }
                                variant="outlined"
                                className={Styles.secondaryButton}
                                fitContent={true}
                                onClick={handlePortfolioClick}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Portfolio Section - Only show if user is logged in */}
            {/* {userData.email && (
                <div className={Styles.portfolioSection}>
                    <h2 className={Styles.portfolioTitle}>Your Staking Portfolio</h2>

                    {portfolioLoading ? (
                        <div className={Styles.portfolioLoading}>
                            <div className={Styles.loadingSkeleton}></div>
                        </div>
                    ) : (
                        <div className={Styles.portfolioStats}>
                            <div className={Styles.portfolioCard}>
                                <div className={Styles.portfolioLabel}>Total Value</div>
                                <div className={Styles.portfolioValue}>
                                    ${portfolioData.total_value_usd.toLocaleString()}
                                </div>
                            </div>

                            <div className={Styles.portfolioCard}>
                                <div className={Styles.portfolioLabel}>Total Earned</div>
                                <div className={Styles.portfolioValue}>
                                    ${portfolioData.total_earned_dollar.toLocaleString()}
                                </div>
                            </div>

                            <div className={Styles.portfolioCard}>
                                <div className={Styles.portfolioLabel}>Monthly Growth</div>
                                <div className={`${Styles.portfolioValue} ${portfolioData.total_earned_percentage_month > 0 ? Styles.positive : ''}`}>
                                    {portfolioData.total_earned_percentage_month.toFixed(2)}%
                                </div>
                            </div>

                            <div className={Styles.portfolioCard}>
                                <div className={Styles.portfolioLabel}>Average APY</div>
                                <div className={Styles.portfolioValue}>
                                    {formatAPY(portfolioData.average_apy)}%
                                </div>
                            </div>
                        </div>
                    )}

                    {portfolioData.active_staking && portfolioData.active_staking.length > 0 && (
                        <div className={Styles.activeStakingSection}>
                            <h3 className={Styles.activeStakingTitle}>Active Staking ({portfolioData.active_staking.length})</h3>
                            <div className={Styles.activeStakingList}>
                                {(portfolioData.active_staking || []).slice(0, 3).map((staking, index) => (
                                    <div key={index} className={Styles.activeStakingItem}>
                                        <div className={Styles.stakingAsset}>
                                            <div
                                                className={Styles.stakingIcon}
                                                style={{
                                                    backgroundColor: getDefaultColor(staking.currency),
                                                    boxShadow: `0 2px 8px ${getDefaultColor(staking.currency)}40`
                                                }}
                                            >
                                                {getDefaultIcon(staking.currency)}
                                            </div>
                                            <span>{staking.currency}</span>
                                        </div>
                                        <div className={Styles.stakingAmount}>
                                            {staking.amount} {staking.asset}
                                        </div>
                                    </div>
                                ))}
                                {portfolioData.active_staking.length > 3 && (
                                    <div className={Styles.moreStaking}>
                                        +{portfolioData.active_staking.length - 3} more
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )} */}

            {/* Staking Pools Section */}
            <div id="staking-pools" className={Styles.poolsSection}>
                <div className={Styles.poolsHeader}>
                    <div className={Styles.poolsHeaderContent}>
                        <h2 className={Styles.poolsTitle}>Staking Pools</h2>
                        <p className={Styles.poolsDescription}>
                            Discover the best staking opportunities with competitive rewards
                        </p>
                    </div>

                    <div className={Styles.searchContainer}>
                        <FiSearch size={16} className={Styles.searchIcon} />
                        <input
                            type="text"
                            placeholder="Search Crypto Currencies"
                            value={searchTerm}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            className={Styles.searchInput}
                        />
                    </div>
                </div>

                {/* Tag Filters */}
                <div className={Styles.categoriesContainer}>
                    {availableTags.map(tag => {
                        const isActive = selectedCategory === tag.id

                        return (
                            <button
                                key={tag.id}
                                onClick={() => handleCategoryChange(tag.id)}
                                className={`${Styles.categoryButton} ${isActive ? Styles.categoryButtonActive : ''}`}
                            >
                                {tag.icon}
                                {String(tag.label || tag.tag || 'Tag')}
                            </button>
                        )
                    })}
                </div>

                {/* Pools Grid */}
                <div className={Styles.poolsGrid}>
                    {stakingLoading ? (
                        // Loading skeleton
                        Array.from({ length: coinsPerPage }).map((_, index) => (
                            <div key={index} className={`${Styles.poolCard} ${Styles.loadingCard}`}>
                                <div className={Styles.loadingSkeleton}></div>
                            </div>
                        ))
                    ) : currentPools.length > 0 ? (
                        currentPools.map((pool) => (
                            <div key={pool.id} className={Styles.poolCard}>
                                {/* Pool Header */}
                                <div className={Styles.poolHeader}>
                                    <div
                                        className={Styles.poolIcon}
                                        style={{
                                            backgroundImage: pool.iconUrl ? `url(${pool.iconUrl})` : 'none',
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                            backgroundRepeat: 'no-repeat'
                                        }}
                                    >
                                        {!pool.iconUrl && getDefaultIcon(pool.coin?.toLowerCase())}
                                    </div>
                                    <div>
                                        <div className={Styles.poolTitleRow}>
                                            <h3 className={Styles.poolName}>{pool.coin}</h3>
                                            {pool.featured && (
                                                <div className={Styles.featuredBadge}>
                                                    <FiStar size={10} />
                                                    <span>FEATURED</span>
                                                </div>
                                            )}
                                        </div>
                                        {/* Display tags (optional) */}
                                        {pool.tags && pool.tags.length > 0 && (
                                            <div className={Styles.tagsList}>
                                                {pool.tags.slice(0, 2).map((tag, idx) => {
                                                    // Find the tag definition to get the icon
                                                    const tagDef = availableTags.find(t => t.id === tag);
                                                    if (!tagDef || tag === 'all') return null;

                                                    return (
                                                        <span key={idx} className={Styles.poolTag}>
                                                            {String(tagDef.label || tagDef.tag || 'Tag')}
                                                        </span>
                                                    );
                                                }).filter(Boolean)}
                                            </div>
                                        )}
                                        <span
                                            className={Styles.poolSymbol}
                                            style={{
                                                backgroundColor: pool.color + '20',
                                                border: `1px solid ${pool.color}40`,
                                                color: pool.color
                                            }}
                                        >
                                            {pool.symbol}
                                        </span>
                                    </div>
                                </div>

                                {/* APR Highlight */}
                                <div className={Styles.apyHighlight}>
                                    <div className={Styles.apyValue}>
                                        Up to {formatAPR(pool.bestAPR)}%
                                    </div>
                                    <div className={Styles.apyLabel}>
                                        Annual Percentage Rate
                                    </div>
                                </div>

                                {/* Available Periods */}
                                <div className={Styles.periodsContainer}>
                                    <div className={Styles.periodsLabel}>
                                        Available Periods
                                    </div>
                                    <div className={Styles.periodTags}>
                                        {(pool.periodsDisplay || []).map((period, index) => (
                                            <div key={index} className={Styles.periodTag}>
                                                {period}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Action Button */}
                                <div className={Styles.buttonContainer}>
                                    <Button
                                        text={
                                            <span className={Styles.buttonContent}>
                                                Start Staking
                                                <FiChevronRight size={16} />
                                            </span>
                                        }
                                        variant="light"
                                        className={Styles.stakingButton}
                                        onClick={() => handleStartStaking(pool)}
                                    />
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className={Styles.noResults}>
                            <FiTarget size={48} />
                            <p className={Styles.noResultsTitle}>No staking pools found</p>
                            <p className={Styles.noResultsDescription}>Try adjusting your search or filter criteria</p>
                        </div>
                    )}
                </div>

                {/* Pagination Controls */}
                {!stakingLoading && filteredPools.length > coinsPerPage && (
                    <div className={Styles.paginationContainer}>
                        <button
                            onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                            disabled={currentPage === 1}
                            className={`${Styles.paginationButton} ${currentPage === 1 ? Styles.paginationButtonDisabled : ''}`}
                        >
                            <FiChevronLeft size={16} />
                            Previous
                        </button>

                        <div className={Styles.pageNumbers}>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                                <button
                                    key={pageNum}
                                    onClick={() => handlePageChange(pageNum)}
                                    className={`${Styles.pageNumber} ${currentPage === pageNum ? Styles.pageNumberActive : ''}`}
                                >
                                    {pageNum}
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className={`${Styles.paginationButton} ${currentPage === totalPages ? Styles.paginationButtonDisabled : ''}`}
                        >
                            Next
                            <FiChevronRight size={16} />
                        </button>
                    </div>
                )}

                {/* Results Info */}
                {!stakingLoading && filteredPools.length > 0 && (
                    <div className={Styles.resultsInfo}>
                        <p>
                            Showing {startIndex + 1}-{Math.min(endIndex, filteredPools.length)} of {filteredPools.length} staking pools
                        </p>
                    </div>
                )}
            </div>

            {/* FAQ Section */}
            <div className={Styles.faqSection}>
                <h2 className={Styles.faqTitle}>Frequently Asked Questions</h2>
                <p className={Styles.faqDescription}>
                    Everything you need to know about crypto staking
                </p>

                <div className={Styles.faqGrid}>
                    <div className={Styles.faqItem}>
                        <h3 className={Styles.faqQuestion}>What is crypto staking?</h3>
                        <p className={Styles.faqAnswer}>
                            Crypto staking is the process of holding cryptocurrencies in a wallet to support the operations of a blockchain network. In return for staking your tokens, you earn rewards, typically in the form of additional tokens.
                        </p>
                    </div>

                    <div className={Styles.faqItem}>
                        <h3 className={Styles.faqQuestion}>How are staking rewards calculated?</h3>
                        <p className={Styles.faqAnswer}>
                            Staking rewards are calculated based on factors like the amount staked, the staking duration, the Annual Percentage Yield (APY), and the network's total staked tokens. Rewards are typically distributed daily or weekly.
                        </p>
                    </div>

                    <div className={Styles.faqItem}>
                        <h3 className={Styles.faqQuestion}>Can I unstake my tokens anytime?</h3>
                        <p className={Styles.faqAnswer}>
                            It depends on the staking period you choose. Flexible staking allows you to unstake anytime, while fixed-term staking (15, 30, 60 days) requires you to wait until the period ends. Early unstaking may result in penalty fees.
                        </p>
                    </div>

                    <div className={Styles.faqItem}>
                        <h3 className={Styles.faqQuestion}>What are the risks of staking?</h3>
                        <p className={Styles.faqAnswer}>
                            Staking risks include market volatility, where the value of your staked tokens may decrease, and potential slashing penalties if the validator misbehaves. Additionally, your tokens are locked for the staking period.
                        </p>
                    </div>

                    <div className={Styles.faqItem}>
                        <h3 className={Styles.faqQuestion}>How do I start staking?</h3>
                        <p className={Styles.faqAnswer}>
                            To start staking, choose a cryptocurrency from our available pools, select your preferred staking period, enter the amount you want to stake, and confirm the transaction. Your rewards will start accumulating immediately.
                        </p>
                    </div>

                    <div className={Styles.faqItem}>
                        <h3 className={Styles.faqQuestion}>What is the minimum amount to stake?</h3>
                        <p className={Styles.faqAnswer}>
                            Minimum staking amounts vary by cryptocurrency. For example, Bitcoin requires a minimum of 0.001 BTC, while Ethereum requires 0.01 ETH. Check each pool for specific minimum requirements.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default StakingMainPage
